import { apiGet, apiPatch, apiPost } from '../client';
import {
  BusinessAccessListResponseSchema,
  BusinessAccessRecordSchema,
  type AccessProfile,
  type BusinessAccessRecord,
  type Role,
} from '../schemas';

/** Accesos: login accounts for the business panel. Owner-only (see docs/CONCEPTS.md). */
export function listAccess(): Promise<{ data: BusinessAccessRecord[] }> {
  return apiGet('/v1/business/users', BusinessAccessListResponseSchema);
}

export type CreateAccessInput = {
  name: string;
  email: string;
  role: Role;
  /** Employees only: what they may use (see features/permissions). */
  accessProfile?: AccessProfile;
  temporaryPassword: string;
};

export function createAccess(
  input: CreateAccessInput,
  idempotencyKey: string,
): Promise<BusinessAccessRecord> {
  return apiPost('/v1/business/users', BusinessAccessRecordSchema, input, idempotencyKey);
}

export function resetAccessPassword(
  accessId: string,
  temporaryPassword: string,
  idempotencyKey: string,
): Promise<BusinessAccessRecord> {
  return apiPatch(
    `/v1/business/users/${encodeURIComponent(accessId)}`,
    BusinessAccessRecordSchema,
    { temporaryPassword },
    idempotencyKey,
  );
}

export function setAccessActive(
  accessId: string,
  isActive: boolean,
  idempotencyKey: string,
): Promise<BusinessAccessRecord> {
  return apiPatch(
    `/v1/business/users/${encodeURIComponent(accessId)}`,
    BusinessAccessRecordSchema,
    { isActive },
    idempotencyKey,
  );
}

/** Links a login to the roster entry it works as (a coach), or back to the whole agenda with null. */
export function setAccessStaffMember(
  accessId: string,
  staffMemberId: string | null,
  idempotencyKey: string,
): Promise<BusinessAccessRecord> {
  return apiPatch(
    `/v1/business/users/${encodeURIComponent(accessId)}`,
    BusinessAccessRecordSchema,
    { staffMemberId },
    idempotencyKey,
  );
}

/**
 * Moves an employee to another profile; what each profile may use lives in
 * /v1/business/permissions. Only a coach works as one roster entry, so leaving
 * Coach also drops that link in the same request.
 */
export function setAccessProfile(
  accessId: string,
  accessProfile: AccessProfile,
  idempotencyKey: string,
  options: { unlinkStaff?: boolean } = {},
): Promise<BusinessAccessRecord> {
  return apiPatch(
    `/v1/business/users/${encodeURIComponent(accessId)}`,
    BusinessAccessRecordSchema,
    { accessProfile, ...(options.unlinkStaff ? { staffMemberId: null } : {}) },
    idempotencyKey,
  );
}
